import './Form.scss'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { ENDPOINTS } from '@/util'
import { CheckboxChecked20Regular, ErrorCircle20Filled } from '@fluentui/react-icons'
import { humanFileSize } from '@/util/humanFileSize'
import { createRestClient } from '@/contexts/QueryProvider'
import { restService } from '@/api/services/RestService'
import { useSessionStorage } from '@/hooks/useSessionStorage'

type FileUploadProps = {
  onSubmit: () => void

  applicationId: number
  projectId: number
  templateId: number
  entityId: number
}

export const FileUpload = (props: FileUploadProps) => {
  const [fileSizeLimit, setFileSizeLimit] = useState<number>(20 * 1024 * 1024)
  const [acceptFiles, setAcceptFiles] = useState<string[]>(['.csv', '.xlsx'])
  const [acceptMimeTypes, setAcceptMimeTypes] = useState<string[]>(['text/csv', '.xlsx'])

  const [validFile, setValidFile] = useState<boolean>(false)
  const [validFileName, setValidFileName] = useState<boolean>(false)
  const [validFileSize, setValidFileSize] = useState<boolean>(false)
  const [validFileType, setValidFileType] = useState<boolean>(false)

  const uploadRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [fileExt, setFileExt] = useState<string>('')

  const [token] = useSessionStorage('token', '')

  const { postStorageApiStorageFileRequirements } = createRestClient()

  const {
    mutate: fileTemplateRequirements,
    // fileTemplateRequirementsPending,
    // fileTemplateRequirementsError,
  } = postStorageApiStorageFileRequirements.useMutation()

  useEffect(() => {
    fileTemplateRequirements(
      {
        query: { applicationId: props.applicationId, templateId: 0 },
      },
      {
        onSuccess(data) {
          setFileSizeLimit(data.fileSizeLimit)
          setAcceptFiles(data.extensionsAllowed)
          setAcceptMimeTypes(data.mimeTypesAllowed)
        },
        onError() {
          toast.error(`Error getting file requirements`)
        },
      }
    )
  }, [fileTemplateRequirements, props.applicationId])

  const validateFile = (file: File | null): boolean => {
    let valid = undefined
    setValidFileType(false)
    setValidFileSize(false)
    setValidFileName(false)

    if (!file) {
      setValidFile(false)
      return false
    }

    if (file.size === 0) {
      toast.error(`File is empty.`)
      valid = false
    } else {
      if (file.size > fileSizeLimit) {
        toast.error(`File size exceeds max size of ${fileSizeLimit / 1024 / 1024} MB`)
        valid = false
      } else {
        setValidFileSize(true)
      }
    }

    const fileName = file?.name || ''
    if (!fileName) {
      toast.error(`Cant determine filename`)
      valid = false
    } else {
      setValidFileName(true)
      const match = fileName.match(/\.([^.]+)$/)
      if (match === null || match.length < 2) {
        toast.error(`Cant find extension`)
        valid = false
      } else {
        const currentFileExt = match[1]
        setFileExt(currentFileExt)
        if (acceptFiles.findIndex((x) => x === '.' + currentFileExt) === -1) {
          toast.error(`Invalid file type selected. Must be ${acceptFiles.join('/')}`)
          valid = false
        } else {
          setValidFileType(true)
        }
      }
    }

    if (valid == undefined) valid = true
    setValidFile(valid)
    return valid
  }

  const handleClick = useCallback(() => {
    uploadRef.current?.click()
  }, [])

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.item(0) ?? null
    setFile(file)
    validateFile(file)
  }, [])

  const onSubmit = () => {
    if (!validateFile(file)) return

    const formData = new FormData()
    formData.append('application_id', props.applicationId.toString())
    formData.append('project_id', props.projectId.toString())
    formData.append('template_id', props.templateId.toString())
    formData.append('entity_id', props.entityId.toString())
    formData.append('file', file!, file?.name)

    fetch(`${ENDPOINTS.DOTNET}${restService.postStorageApiUploadFile.schema.url}`, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
      body: formData,
    })
      .then(async (resp) => {
        if (!resp.ok) {
          const errorJson = await resp.json()
          throw new Error(errorJson.Message)
        }

        toast.info(`File upload complete: ${file?.name}`)
        props.onSubmit()
      })
      .catch((error) => {
        console.warn('File upload failed: ', error)
        toast.error(`File upload failed : ${error}`)
      })
  }

  const ValidBox = () => <CheckboxChecked20Regular color="#175d2d" />
  const ErrorBox = () => <ErrorCircle20Filled color="#e0301e" />

  return (
    <div className="upload-file">
      <input
        ref={uploadRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleChange}
        hidden
        accept={acceptFiles.concat(acceptMimeTypes).join(',')}
      />
      <Button text={'Choose a file to upload'} onClick={handleClick} />
      {file && (
        <div className="file-validation">
          <div>
            <div>File</div>
            <div>{file.name}</div>
            <div>{validFileName ? <ValidBox /> : <ErrorBox />}</div>

            <div>Size</div>
            <div>{humanFileSize(file.size)}</div>
            <div>{validFileSize ? <ValidBox /> : <ErrorBox />}</div>

            <div>Type</div>
            <div>{fileExt}</div>
            <div>{validFileType ? <ValidBox /> : <ErrorBox />}</div>
          </div>
        </div>
      )}

      {file && <Button text="Upload" onClick={onSubmit} disabled={!validFile} />}
    </div>
  )
}
